import colorSyntax from "@toast-ui/editor-plugin-color-syntax";
import { Editor } from "@toast-ui/react-editor";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { BuildingModel, CommonFacilityModel } from "src/api/building/building-types";
import { ApplicationFormModel } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { MbMember } from "src/api/member/member-types";
import { getProductApplicationFormAsync } from "src/api/product/product-api";
import { QuotationItem } from "src/api/reservation/reservation-type";
import noImage from "src/assets/images/noImage_default.svg";
import { BaseButton, ResizedImage } from "src/components";
import ContractFileUpload from "src/components/ContractFileUpload";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import usePostMessage from "src/pages/hooks/post-message";
import { useModal } from "src/recoil/modalState/hook";
import { formatPhoneNumber, getUseTimeDiff, ViewYmdFormat } from "src/utils/common-util";
import useCustomLinkAction from "./useCustomLinkAction";

type Props = {
  quotation: QuotationItem;
  facility: CommonFacilityModel;
  building: BuildingModel;
  member: MbMember;
  savedFormData: ApplicationFormModel;
  productId: number;
  onChangeFormData: (formData: ApplicationFormModel) => void;
  onApplyContract: (formData?: ApplicationFormModel) => Promise<void>;
};

const ContractApplicationForm = ({ quotation, facility, building, member, onApplyContract, productId }: Props) => {
  const { t } = useTranslation(["contract", "common"]);
  const { setBaseModal } = useModal();
  const editorRef = useRef<Editor>(null);

  const { tooltip:linkPreviewTooltip, onEditorLoad } = useCustomLinkAction(editorRef);

  const location = useLocation();
  const navigate = usePartnerNavigate();
  const { goBack } = useNavigateGoBack();
  const [formData, setFormData] = useState<ApplicationFormModel>();

  const [isRequitAttachment, setIsRequitAttachment] = useState<boolean>(false);

  const colorSyntaxOptions = useMemo(() => {
    return {
      preset: ["#FF695B", "#26A687", "#FFD335", "#191919", "#525252", "#767676", "#999999", "#DBDBDB", "#E0E0E0", "#F2F4F6", "#F8F8FA", "#FFFFFF"],
    };
  }, []);

  const { executeAsync: getProductApplicationForm } = useApiOperation(getProductApplicationFormAsync);
  const { postMessageGoBackNavigation } = usePostMessage();

  const getProductApplicationFormData = async () => {
    const { data } = await getProductApplicationForm({ productId: Number(productId) });
    if (data?.data?.applicationForm) {
      const retuls = data.data.applicationForm.formData;
      setFormData(retuls);
      setIsRequitAttachment(data.data.applicationForm.formData.attachmentConfig?.isRequired || false);
    }
  };

  useEffect(() => {
    if (productId) {
      getProductApplicationFormData();
    }
  }, [productId]);

  // 에디터 내용 변경
  const onChangeValue = () => {

    const editorInstance = editorRef.current?.getInstance();

    // editor value를 markdown 형식으로 onChnage
    // const markDown = editorInstance?.getMarkdown();
    // setFormData((prev) => ({ ...prev, richText: markDown || "" }));

    // HTML 형식으로 저장하여 줄바꿈 보존
    const htmlContent = editorInstance?.getHTML();
    setFormData((prev) => ({ ...prev, richText: htmlContent || "" }));
  };

  // 상단에 X 버튼 동작
  const handleGoBack = () => {
    if (!window.ReactNativeWebView) {
      if (location.state && location.state === "mypage") {
        navigate("/court/mypage/contracts");
      } else if (location.state && location.state === "quotation") {
        navigate(`/court/mypage/main`);
      } else {
        setBaseModal({
          isOpen: true, //
          title: t("applicationForm.cancelWritingConfirm"),
          children: (
            <p>
              <Trans t={t} i18nKey="applicationForm.unsavedInfoWarning" components={{ br: <br /> }} />
            </p>
          ),
          btnLeftTitle: t("common:cancel"),
          btnRightTitle: t("common:confirm"),
          onClick: () => {
            goBack();
            setBaseModal({ isOpen: false });
          },
          onClose: () => setBaseModal({ isOpen: false }),
        });
      }
    } else {
      postMessageGoBackNavigation();
    }
  };

  // 예약하기 버튼 동작 > 계약 생성
  const handleApplyContract = () => {
    if (isRequitAttachment && (formData?.attachments?.length || 0) === 0) {
      setBaseModal({
        isOpen: true,
        title: t("applicationForm.attachmentRequired"),
        btnRightTitle: t("common:confirm"),
        onClick: () => setBaseModal({ isOpen: false }),
      });

      return;
    }
    setBaseModal({
      isOpen: true,
      title: t("applicationForm.confirmReservation"),
      btnLeftTitle: t("common:cancel"),
      btnRightTitle: t("common:confirm"),
      onClose: () => setBaseModal({ isOpen: false }),
      onClick: () => {
        onApplyContract({
          ...formData!,
          attachments: formData?.attachments?.map((file) => {
            const { blobUrl, ...updataFile } = file;

            return {
              ...updataFile,
              cmdType: "U",
              serviceType: "CT_APPLICATION",
              serviceTag: "APPLICATION_ATTACHMENT",
            };
          }),
        });
        setBaseModal({ isOpen: false });
      },
    });
  };

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header
        headerType="CLOSE"
        // title="상세 내역"
        title={t("applicationForm.title")}
        onClickCloseButton={handleGoBack}
      />
      <div className="contract-page">
        <section className="contract-main ">
          <div className="gray-card">
            <div className="gray-card__product">
              <ResizedImage
                className="minmax60"
                url={
                  facility?.mediaList && facility?.mediaList.length > 0
                    ? facility.mediaList.find((media) => media.isPrimary)?.url || facility?.mediaList![0].url || noImage
                    : noImage
                }
                alt={t("applicationForm.facilityImageAlt", { name: facility?.facilityName })}
              />
              <div className="gray-card__contents">
                <p className="gray-card__contents-title">{facility?.facilityName}</p>
                <p className="gray-card__contents-address">{building.buildingName}</p>
              </div>
            </div>
            <div className="gray-card__info">
              <>
                <div className="base-section-layout-line">
                  <p className="base-section-layout-index text-primary3">{t("applicationForm.usagePeriod")}</p>
                  <p className="base-section-layout-value">{`${
                    quotation?.startDate && moment(quotation?.startDate).format(ViewYmdFormat.YYYY_MM_DD)
                  }(${moment(quotation?.endDate).format("ddd")})`}</p>
                </div>
                <div className="base-section-layout-line">
                  <p className="base-section-layout-index text-primary3">{t("applicationForm.usageTime")}</p>
                  <p className="base-section-layout-value">
                    {`${quotation?.startDate && moment(quotation?.startDate).format("HH:mm")} ~
                      ${quotation?.endDate && moment(quotation?.endDate).format("HH:mm")}
                      ${getUseTimeDiff(quotation?.startDate, quotation?.endDate)}`}
                  </p>
                </div>
              </>
            </div>
          </div>
        </section>

        <section className="base-section-layout top-divider">
          <div className="base-section-layout__container">
            <div className="base-section-layout__wrap">
              <div className="base-section-layout__title-wrap">
                <h2>{t("applicationForm.applicantInformation")}</h2>
              </div>
              <div className="base-section-layout-line">
                <p className="base-section-layout-index text-primary3">{t("applicationForm.nameNickname")}</p>
                <p className="base-section-layout-value">{member?.nickname || "-"}</p>
              </div>
              <div className="base-section-layout-line">
                <p className="base-section-layout-index text-primary3">{t("applicationForm.mobileNumber")}</p>
                <p className="base-section-layout-value">{formatPhoneNumber(member?.phoneNumber || "")}</p>
              </div>
              <div className="base-section-layout-line">
                <p className="base-section-layout-index text-primary3">{t("applicationForm.email")}</p>
                <p className="base-section-layout-value">{member?.email}</p>
              </div>
            </div>
          </div>
        </section>

        {formData && (
          <section className="base-section-layout top-divider">
            <div className="base-section-layout__container">
              <div className="base-section-layout__wrap">
                <div className="base-section-layout__title-wrap pb10">
                  <h2>{t("applicationForm.additionalInformation")}</h2>
                </div>
                <div className="base-markdown">
                  {/* @ts-ignore*/}
                  <Editor
                    ref={editorRef}
                    initialValue={formData.richText}
                    previewStyle="tab"
                    height="auto"
                    initialEditType="wysiwyg"
                    useCommandShortcut={true}
                    onChange={onChangeValue}
                    onLoad={onEditorLoad}
                    usageStatistics={false}
                    toolbarItems={[]}
                    plugins={[[colorSyntax, colorSyntaxOptions]]}
                  />
                  {linkPreviewTooltip}
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="base-section-layout">
          <div className="base-section-layout__container pt0">
            <div className="base-section-layout__wrap">
              <div className="base-section-layout__title-wrap pb10">
                <h2 className={`${isRequitAttachment ? "required" : ""}`}>{t("applicationForm.attachment")}</h2>
              </div>

              <ContractFileUpload
                fileList={formData?.attachments || []}
                limit={10}
                multiple={true}
                setFiles={(files) => setFormData((prev) => (prev ? { ...prev, attachments: files || [] } : undefined))}
              />
            </div>
          </div>
        </section>
      </div>
      <div className="base-floating-btn-wrap border-top ">
        <BaseButton title={t("applicationForm.reserve")} onClick={handleApplyContract} />
      </div>
    </>
  );
};

export default ContractApplicationForm;
